import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatBRL } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Flame, Snowflake, AlertCircle, Users, Building2, Sparkles, ArrowRight, MessageCircle, Clock, BookOpen, X, Zap } from "lucide-react";

const stageColors = {
  novo: "bg-blue-100 text-blue-700",
  em_atendimento: "bg-cyan-100 text-cyan-700",
  qualificado: "bg-purple-100 text-purple-700",
  visita: "bg-amber-100 text-amber-700",
  proposta: "bg-orange-100 text-orange-700",
  fechamento: "bg-lime-100 text-lime-700",
  ganho: "bg-emerald-100 text-emerald-800",
  perdido: "bg-stone-200 text-stone-600",
};

const stageLabels = {
  novo: "Novo", em_atendimento: "Em atend.", qualificado: "Qualificado", visita: "Visita",
  proposta: "Proposta", fechamento: "Fechamento", ganho: "Ganho", perdido: "Perdido"
};

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWelcome, setShowWelcome] = useState(() => {
    return localStorage.getItem("corretor_welcome_dismissed") !== "1";
  });

  const isNewUser = (summary?.total_leads || 0) === 0 && (summary?.total_properties || 0) === 0;

  const dismissWelcome = () => {
    localStorage.setItem("corretor_welcome_dismissed", "1");
    setShowWelcome(false);
  };

  useEffect(() => {
    Promise.all([api.get("/dashboard/summary"), api.get("/dashboard/daily-brief")])
      .then(([s, b]) => { setSummary(s.data); setBrief(b.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-5 lg:p-10 space-y-4">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 lg:p-10 space-y-6 max-w-7xl mx-auto" data-testid="dashboard-root">
      {/* Greeting */}
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#D4651A] font-bold mb-1">Painel</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C19]">
          Olá, {user?.name?.split(" ")[0]}.
        </h1>
        <p className="text-stone-500 mt-1">Aqui está sua agenda do dia.</p>
      </div>

      {/* Welcome banner for new users */}
      {showWelcome && isNewUser && (
        <Card data-testid="welcome-banner" className="relative bg-gradient-to-br from-[#D4651A] to-[#A04E10] text-white border-0 p-6 rounded-2xl overflow-hidden">
          <button
            onClick={dismissWelcome}
            data-testid="welcome-banner-dismiss"
            className="absolute top-3 right-3 text-white/70 hover:text-white p-1"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wider text-white/80 mb-1 font-semibold">Comece por aqui</div>
              <h2 className="font-heading text-xl font-bold">Guia de boas-vindas em 5 passos</h2>
              <p className="text-white/90 text-sm mt-2 leading-relaxed">
                Saí de zero em 15 minutos. Cadastra teus leads, configura WhatsApp, pega tua primeira copy pronta. Eu te guio.
              </p>
              <Link to="/app/onboarding">
                <Button data-testid="welcome-banner-cta" className="mt-4 bg-white text-[#1B3A2F] hover:bg-white/90 rounded-full font-semibold">
                  Abrir guia <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      )}

      {/* Daily brief card */}
      <Card className="bg-gradient-to-br from-[#1B3A2F] to-[#244A3C] text-white border-0 p-6 rounded-2xl" data-testid="daily-brief-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[#D4651A] flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-white/70 mb-1">estatebroker.AI · resumo do dia</div>
            <div className="text-white whitespace-pre-line text-sm leading-relaxed" data-testid="daily-brief-text">
              {brief?.brief || "Tudo certo por aqui."}
            </div>
            <Link to="/app/chat">
              <Button variant="ghost" className="mt-4 text-white hover:bg-white/10 -ml-3" data-testid="open-chat-btn">
                <MessageCircle className="w-4 h-4 mr-2" /> Conversar com o assistente
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </Card>

      {/* Stats grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Leads ativos" value={summary?.total_leads || 0} testid="stat-leads" link="/app/leads" />
        <StatCard icon={Building2} label="Imóveis" value={summary?.total_properties || 0} testid="stat-properties" link="/app/imoveis" />
        <StatCard icon={Flame} label="Leads quentes" value={summary?.hot_leads?.length || 0} testid="stat-hot" accent link="/app/leads?temp=quente" />
        <StatCard icon={AlertCircle} label="Precisam atenção" value={summary?.needs_attention?.length || 0} testid="stat-attention" warning />
      </div>

      {/* Funil status */}
      <Card className="p-6 rounded-2xl border-stone-200" data-testid="funnel-status-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold">Funil de vendas</h2>
          <Link to="/app/leads" className="text-sm text-[#1B3A2F] font-semibold hover:underline">Ver detalhes →</Link>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {Object.entries(stageLabels).map(([key, label]) => (
            <div key={key} className="border border-stone-200 rounded-xl p-3 text-center" data-testid={`funnel-stage-${key}`}>
              <Badge className={`${stageColors[key]} hover:${stageColors[key]} mb-2 text-xs font-medium`}>{label}</Badge>
              <div className="font-heading text-2xl font-bold text-[#1C1C19]">
                {summary?.stages?.[key] || 0}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Needs attention */}
      <Card className="p-6 rounded-2xl border-stone-200" data-testid="needs-attention-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-xl font-bold">Precisam de você hoje</h2>
            <p className="text-sm text-stone-500 mt-0.5">Leads parados ou com follow-up vencido</p>
          </div>
          <Link to="/app/leads" className="text-sm text-[#1B3A2F] font-semibold hover:underline">Ver todos →</Link>
        </div>
        {summary?.needs_attention?.length === 0 ? (
          <div className="text-center py-8 text-stone-500">
            <Sparkles className="w-8 h-8 mx-auto mb-2 text-[#D4651A]" />
            Carteira em dia! Que tal prospectar novos leads?
          </div>
        ) : (
          <div className="space-y-2">
            {summary?.needs_attention?.slice(0, 5).map((lead) => (
              <div key={lead.id} className="flex items-center justify-between p-3 border border-stone-200 rounded-lg hover:bg-stone-50 transition" data-testid={`attention-lead-${lead.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-[#1B3A2F] text-white flex items-center justify-center font-semibold flex-shrink-0">
                    {lead.name[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{lead.name}</div>
                    <div className="text-xs text-stone-500 flex items-center gap-1 flex-wrap">
                      <Clock className="w-3 h-3" /> {lead.reason}
                      {lead.budget > 0 && <span>· {formatBRL(lead.budget)}</span>}
                    </div>
                    {lead.next_action && (
                      <div data-testid={`attention-next-action-${lead.id}`}
                        className="text-xs text-[#1B3A2F] font-medium flex items-start gap-1 mt-0.5">
                        <Zap className="w-3 h-3 text-[#D4651A] mt-0.5 flex-shrink-0" />
                        <span className="truncate">{lead.next_action.text}</span>
                      </div>
                    )}
                  </div>
                </div>
                <Badge className={`${stageColors[lead.stage]} flex-shrink-0`}>{stageLabels[lead.stage]}</Badge>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, link, accent, warning, testid }) {
  const Inner = (
    <Card
      data-testid={testid}
      className={`p-5 rounded-2xl border transition hover:-translate-y-0.5 hover:shadow-md cursor-default ${
        accent ? "border-[#D4651A]/40 bg-[#D4651A]/5" : warning ? "border-amber-300 bg-amber-50" : "border-stone-200 bg-white"
      }`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-stone-500 uppercase tracking-wider font-semibold">{label}</div>
          <div className="font-heading text-3xl font-extrabold text-[#1C1C19] mt-1">{value}</div>
        </div>
        <Icon className={`w-7 h-7 ${accent ? "text-[#D4651A]" : warning ? "text-amber-600" : "text-stone-400"}`} />
      </div>
    </Card>
  );
  return link ? <Link to={link}>{Inner}</Link> : Inner;
}
